import { QueryClient } from 'react-query'
import { clearUserIdentityCache } from 'shared/administration/api/identity'
import { requestOrganizationSwitch } from 'shared/api/organizationSession'

import { forceClientLogout } from '../redux/actions/auth/authActions'
import { LOGOUT_USER, USER_AUTHENTICATION_SUCCESS } from '../redux/actions/auth/authActionTypes'
import { resumeOrganizationPersistence } from '../redux/store/organizationPersistence'
import { persistor } from '../redux/store/persistor'
import { store } from '../redux/store/store'

async function transitionOrganization(queryClient: QueryClient, switchSession?: () => Promise<unknown>) {
  // Advance the thunk generation before clearing caches. Late results must not repopulate either
  // the current document or persisted state while the new organization is being loaded.
  // Unmount the old app before authentication is restored for the new document.
  window.dispatchEvent(new Event('organization-switching'))
  try {
    if (switchSession) store.dispatch(forceClientLogout())
    else {
      // Reconciliation must not remove another tab's shared login marker.
      clearUserIdentityCache()
      store.dispatch({ type: LOGOUT_USER })
    }
    await queryClient.cancelQueries()
    queryClient.clear()
    await persistor.flush()
    await persistor.purge()
    // Only start the session change once the old account is gone, including persisted state.
    await switchSession?.()
    // Keep this document's old membership header until it unloads.
    localStorage.setItem('isAuthenticated', 'true')
    resumeOrganizationPersistence()
    persistor.persist()
    store.dispatch({ type: USER_AUTHENTICATION_SUCCESS })
    await persistor.flush()
    window.location.replace('/')
  } catch {
    // Keep private content unmounted even if browser storage fails; offer a fresh document.
    queryClient.clear()
    window.dispatchEvent(new Event('organization-switch-failed'))
  }
}

export async function reloadOrganization(queryClient: QueryClient) {
  await transitionOrganization(queryClient)
}

export async function switchOrganization(membership: string, current: string, queryClient: QueryClient) {
  // Full navigation still waits for the server to finish rotating the session cookie.
  await transitionOrganization(queryClient, () => requestOrganizationSwitch(membership, current))
}
